/**
 * configTouched.ts — the SAVE PAYLOAD of the rider config.
 *
 * Why this exists (measured 2026-10-15): `useConfig` used to PUT the WHOLE
 * config (every section × every transport) on each autosave while declaring
 * `__touched` = the units the user had really edited. The server
 * (`server/lib/riderConfigMerge.js`, `planConfigWrite`) keeps the STORED value
 * for every unit absent from `__touched` — so the untouched units in the body
 * were ignored anyway, and shipping them had one visible cost: a unit that is
 * empty in the client state arrived empty, which looks like "I want to wipe
 * this section", so the anti-loss guard fired a `preserved` warning. One
 * backline edit produced EIGHT such warnings on screen — success messages that
 * drowned the banner which also carries the 409 conflict (the case where the
 * user's work was NOT saved).
 *
 * Sending only the touched units is therefore equivalent in outcome, lighter on
 * the wire, and removes the false alarms. `__touched` keeps listing exactly the
 * units present in the payload — that is the contract with the server.
 *
 * ⚠️ An EMPTY `touched` list must keep the historical FULL payload: that is the
 * deliberate reset path (`forceFullRef`) and the retro-compatible path of an
 * older cached bundle, both of which expect a full replace.
 */

/** Config shape: a section may hold one value or one per transport mode. */
type AnyConfig = Record<string, any>;

/**
 * Reduce a full config to the units listed in `touched`.
 *
 * A touched entry is either a whole section (`'rider'`, `'globalRacks'`,
 * `'setlistPdf'`) or one section × transport (`'backline.van'`). Unknown
 * entries and undefined values are skipped — never invented.
 */
export function pickTouchedUnits<T extends AnyConfig>(config: T, touched: string[]): Partial<T> {
  const out: AnyConfig = {};
  for (const unit of touched) {
    if (!unit) continue;
    const dot = unit.indexOf('.');
    const section = dot === -1 ? unit : unit.slice(0, dot);
    const transport = dot === -1 ? null : unit.slice(dot + 1);
    const value = config?.[section];
    if (value === undefined) continue;
    if (transport === null) {
      out[section] = value;
      continue;
    }
    const perTransport = value?.[transport];
    if (perTransport === undefined) continue;
    if (out[section] === undefined) out[section] = {};
    out[section][transport] = perTransport;
  }
  return out as Partial<T>;
}
